import React, { Component, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';

import Slider from 'react-slick';
import '../../styles/slick/slick.css';
import '../../styles/slick/slick-theme.css';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import {
  H3,
  H5,
  LayoutSingleColumn,
  ModalInMobile,
  NamedRedirect,
  Page,
  SearchNavBar,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import Icons from '../../components/Icons/Icons';
import FilterModal from './FilterModal/FilterModal';
import { getCategoriesByType, isArrayLength } from '../../util/genericHelpers';
import TabSwipeable from './TabSwipeable';
import GlobalBottomNavMobile from '../../components/GlobalBottomNavMobile/GlobalBottomNavMobile';
import { hostCategories, serviceCategories } from '../../util/dataset';
import Drawer from 'react-modern-drawer';
import './Drawer.css';
import { types as sdkTypes } from '../../util/sdkLoader';
import { SLICK_SLIDER_SETTINGS } from '../../util/constants';
import { SERVICE_PROVIDER_DEFAULT_FILTER } from './searchDataset';
import { BASE_LISTING_USER_TYPES, ListingFieldConfigKey } from '../../util/enums';
import { stringify } from 'query-string';

const { LatLng, LatLngBounds } = sdkTypes;

// Define enum for view options
export const ViewOptions = {
  LIST: 'list',
  MAP: 'map',
};

const tabs = [
  { key: BASE_LISTING_USER_TYPES.HOST, label: 'Explore Listings' },
  { key: BASE_LISTING_USER_TYPES.PROVIDER, label: 'Find Service Providers' },
];

const MODAL_BREAKPOINT = 1024; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Category Filter Component

const CategorySlider = ({
  activeTab,
  hostCategories,
  serviceCategories,
  history,
  searchParamsInURL,
}) => {
  const categories =
    activeTab === BASE_LISTING_USER_TYPES.HOST ? hostCategories : serviceCategories;

  const handleCategoryClick = searchKey => {
    // Parse new params from category's searchKey (e.g. ?category=some-value)
    const newParams = parse(searchKey.startsWith('?') ? searchKey : `?${searchKey}`);

    // Merge with existing params
    const mergedParams = {
      ...searchParamsInURL,
      ...newParams,
    };

    const queryString = stringify(mergedParams);
    history.push(`/?${queryString}`);
  };

  return (
    <div className={css.categoryFilterSlider}>
      <Slider {...SLICK_SLIDER_SETTINGS}>
        {categories.map((category, index) => (
          <div
            key={index}
            className={classNames(css.categoryBlock, category.active ? css.active : '')}
            role="button"
            onClick={() => handleCategoryClick(category.searchKey)}
          >
            <div className={css.categoryIcon}>
              {category?.isImage ? (
                <img src={category.icon} alt={category.text} />
              ) : (
                <Icons name={category.icon} />
              )}
            </div>
            <div className={css.categoryText}>{category.text}</div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

// Search Tabs Component
const SearchTabs = ({ tabs, activeTab, setActiveTab }) => (
  <div className={css.searchTabType}>
    {tabs.map((tab, index) => (
      <button
        key={index}
        className={classNames({ [css.active]: activeTab === tab.key })}
        onClick={() => setActiveTab(tab.key)}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// Filters Icon Button
const FiltersButton = ({ isOpen, openFiltersDrawer }) => (
  <div
    className={classNames(css.filterIcon, { [css.opened]: isOpen })}
    onClick={() => openFiltersDrawer(true)}
  >
    <Icons name="filter" /> Filters
  </div>
);

// View Toggle (Map/List)
const ViewToggle = ({ currentView, onChangeSearchView }) => (
  <div
    className={css.showHideMap}
    onClick={() =>
      onChangeSearchView(currentView === ViewOptions.LIST ? ViewOptions.MAP : ViewOptions.LIST)
    }
  >
    {currentView === 'map' ? (
      <span className={css.icon}>
        Show List <Icons name="list" />
      </span>
    ) : (
      <span className={css.icon}>
        Show Map <Icons name="map" />
      </span>
    )}
  </div>
);

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      currentView: ViewOptions.LIST ? ViewOptions.LIST : ViewOptions.MAP,
      activeTab: BASE_LISTING_USER_TYPES.HOST,
      isOpen: false,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.onChangeSearchView = this.onChangeSearchView.bind(this);
    this.openFiltersDrawer = this.openFiltersDrawer.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.toggleDrawer = this.toggleDrawer.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }
  toggleDrawer = () => {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  };
  setActiveTab = key => {
    this.setState({ activeTab: key }, () => {
      const searchParams = new URLSearchParams();

      if (key === BASE_LISTING_USER_TYPES.PROVIDER) {
        searchParams.set('pub_listingType', BASE_LISTING_USER_TYPES.PROVIDER);
      }

      const searchQuery = searchParams.toString();
      const path = searchQuery ? `/?${searchQuery}` : '/';

      this.props.history.push(path);
    });
  };

  openFiltersDrawer(boolean) {
    this.setState({ filterDrawer: boolean });
  }
  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );
    this.openFiltersDrawer(false);

    if (this.state.roomsFilter) {
      const filterMap = {
        beds: 'pub_beds',
        bedrooms: 'pub_bedrooms',
        bathrooms: 'pub_bathrooms',
      };

      Object.entries(filterMap).forEach(([key, searchKey]) => {
        if (this.state.roomsFilter[key]) {
          search[searchKey] = this.state.roomsFilter[key];
        }
      });
    }

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.openFiltersDrawer(false);
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });
    this.openFiltersDrawer(false);
    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }
  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
      };

      // parse query parameters, including a custom attribute named category
      const { address, bounds, mapSearch, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }
  onChangeSearchView(type) {
    this.setState({ currentView: type });
  }
  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      showTopbarSearch,
      heroRef,
      history,
      setbottomSheetOpen,
      bottomSheetOpen,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
    ];
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, {
          listingFieldsConfig: customSecondaryFilters,
          defaultFiltersConfig: [],
          listingCategories,
        })
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const {
      bounds = new LatLngBounds(
        new LatLng(42.342346, -84.784535),
        new LatLng(37.771766, -88.097892)
      ),
      origin,
    } = searchParamsInURL || {};

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // const mainCategoryes

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    const { activeTab, filterDrawer, currentView, isSearchMapOpenOnMobile } = this.state;
    const serviceProviderFiltersMaybe =
      getCategoriesByType(config.categoryConfiguration.categories, activeTab) || [];

    // Extract common filters
    const priceFilter =
      isArrayLength(defaultFiltersConfig) && defaultFiltersConfig.find(d => d?.key === 'price');

    const isProvider = activeTab === BASE_LISTING_USER_TYPES.PROVIDER;

    const serviceCategoryKey = ListingFieldConfigKey.RELATED_SERVICE_CATEGORIES;

    // Determine which listing fields to include
    const listingFieldFilters = listingFieldsConfig.filter(f =>
      isProvider ? f?.key === serviceCategoryKey : f?.key !== serviceCategoryKey
    );

    // Construct final filters array
    const filters = [priceFilter, ...listingFieldFilters]
      .filter(Boolean) // Remove null/undefined
      .filter(f => f.key !== 'type' && f.key !== 'category'); // Remove always-excluded filters

    const filterModal = (
      <FilterModal
        id="SearchPageWithMap.filterModal"
        isOpen={this.state.filterDrawer}
        onClose={() => this.openFiltersDrawer(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        modalTitle="Filters"
      >
        <SearchFiltersSecondary
          urlQueryParams={validQueryParams}
          listingsAreLoaded={listingsAreLoaded}
          applyFilters={this.applyFilters}
          cancelFilters={this.cancelFilters}
          resetAll={this.resetAll}
          onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
        >
          {filters.map(config => {
            return (
              <FilterComponent
                key={`SearchFiltersSecondary.${config.key}`}
                idPrefix="SearchFiltersSecondary"
                config={config}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={validQueryParams}
                onFilterChange={this.onRoomsFilterChange}
                initialValues={initialValues(this.props, this.state.currentQueryParams)}
                getHandleChangedValueFn={this.getHandleChangedValueFn}
                intl={intl}
                serviceProviderFiltersMaybe={serviceProviderFiltersMaybe}
                showAsPopup={false}
              />
            );
          })}
        </SearchFiltersSecondary>
      </FilterModal>
    );
    const searchPanel = (
      <SearchResultsPanel
        className={css.searchListingsPanel}
        listings={listings}
        pagination={pagination}
        search={parse(location.search)}
        setActiveListing={onActivateListing}
        isMapVariant
        isServiceListingCard={activeTab === BASE_LISTING_USER_TYPES.PROVIDER}
        isMobileLayout={isMobileLayout}
        bottomSheetOpen={bottomSheetOpen}
        setbottomSheetOpen={setbottomSheetOpen}
        setCurrentView={v => this.setState({ currentView: v })}
      />
    );

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          classNames={this.state.currentView === 'map' ? css.mapViewContainer : null}
          topbar={
            <TopbarContainer
              showTopbarSearch={showTopbarSearch}
              rootClassName={topbarClasses}
              currentSearchParams={validQueryParams}
            />
          }
          footer={!isMobileLayout ? <FooterContainer /> : null}
        >
          <div
            className={classNames(css.container, currentView === 'map' ? css.mapContainer : null)}
            ref={heroRef}
          >
            {isMobileLayout ? (
              <div className={css.mobileSearchFilterSec}>
                <div className={css.rightFilters}>
                  <div className={css.topFilters}>
                    <SearchTabs
                      tabs={tabs}
                      activeTab={activeTab}
                      setActiveTab={this.setActiveTab}
                    />
                    <div className={css.mobileSearchDrawer}>
                      <button className={css.searchIcon} onClick={this.toggleDrawer}>
                        <Icons name="search" />
                      </button>
                      <FiltersButton
                        isOpen={filterDrawer}
                        openFiltersDrawer={this.openFiltersDrawer}
                      />
                    </div>
                  </div>
                  <CategorySlider
                    searchParamsInURL={searchParamsInURL}
                    history={history}
                    activeTab={activeTab}
                    hostCategories={hostCategories}
                    serviceCategories={serviceCategories}
                  />
                </div>
                <Drawer
                  open={this.state.isOpen}
                  onClose={this.toggleDrawer}
                  direction="left"
                  className="bla bla2"
                >
                  <div className={css.searchContainer}>
                    <h3>
                      <span className={css.back} onClick={this.toggleDrawer}>
                        <span className={css.backIcon}>
                          <Icons name="back" />
                        </span>{' '}
                        <FormattedMessage id="SearchPage.filterLabel" />
                      </span>
                    </h3>
                    <SearchNavBar />
                  </div>
                </Drawer>
              </div>
            ) : (
              <div className={css.desktopSearchFilterSec}>
                <div className={css.leftFilters}>
                  <SearchTabs tabs={tabs} activeTab={activeTab} setActiveTab={this.setActiveTab} />
                  {!showTopbarSearch && (
                    <div className={css.searchContainer}>
                      <SearchNavBar />
                    </div>
                  )}
                </div>
                <div className={css.rightFilters}>
                  <CategorySlider
                    searchParamsInURL={searchParamsInURL}
                    history={history}
                    activeTab={activeTab}
                    hostCategories={hostCategories}
                    serviceCategories={serviceCategories}
                  />
                  <FiltersButton isOpen={filterDrawer} openFiltersDrawer={this.openFiltersDrawer} />
                </div>
              </div>
            )}

            <div className={css.mainContentWrapper}>
              <div
                className={classNames(css.searchResultContainer, {
                  [css.mapView]: currentView === ViewOptions.MAP,
                })}
              >
                {currentView === ViewOptions.MAP ? (
                  <div className={css.mapWrapper} data-testid="searchMapContainer">
                    <SearchMap
                      reusableContainerClassName={css.map}
                      rootClassName={css.mapRoot}
                      activeListingId={activeListingId}
                      bounds={bounds}
                      center={origin}
                      isSearchMapOpenOnMobile={isSearchMapOpenOnMobile}
                      location={location}
                      listings={listings || []}
                      onMapMoveEnd={this.onMapMoveEnd}
                      onCloseAsModal={() => onManageDisableScrolling('SearchPage_map', false)}
                      messages={intl.messages}
                    />
                  </div>
                ) : (
                  <div
                    className={classNames(css.listingsForMapVariant, {
                      [css.newSearchInProgress]: !(listings || searchListingsError),
                    })}
                  >
                    {searchListingsError && (
                      <H3 className={css.error}>
                        <FormattedMessage id="SearchPage.searchError" />
                      </H3>
                    )}
                    {!isValidDatesFilter && (
                      <H5>
                        <FormattedMessage id="SearchPage.invalidDatesFilter" />
                      </H5>
                    )}
                    {searchPanel}
                  </div>
                )}

                {filterModal}
                {currentView === ViewOptions.LIST && <GlobalBottomNavMobile />}
                {isMobileLayout ? (
                  <TabSwipeable
                    listings={listings}
                    isMobileLayout={isMobileLayout}
                    onChangeSearchView={this.onChangeSearchView}
                    currentView={currentView}
                  />
                ) : (
                  <ViewToggle
                    currentView={currentView}
                    onChangeSearchView={this.onChangeSearchView}
                  />
                )}
              </div>
            </div>
            {/* {noResultsInfo} */}
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with map layout
 *
 * @param {Object} props
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onActivateListing] - The function to activate a listing
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [showTopbarSearch, setShowTopbarSearch] = useState(false);
  const heroRef = useRef(null);
  const [bottomSheetOpen, setbottomSheetOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const { bottom, top } = heroRef.current.getBoundingClientRect();
        setShowTopbarSearch(top < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Trigger once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      setShowTopbarSearch={setShowTopbarSearch}
      showTopbarSearch={showTopbarSearch}
      heroRef={heroRef}
      setbottomSheetOpen={setbottomSheetOpen}
      bottomSheetOpen={bottomSheetOpen}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
